import React, { useState } from 'react';
import { Button, Dialog } from '@mui/material';
import { entityNameMinimumLength, validateAlphaNumeric, validateEntityName } from 'const/regex';
import { Store } from '@restore/services-lib';
import { CreateStoreInterface, useStoresContext } from 'pages/Stores/stores.context';
import Form from 'components/Form/Form';
import { FormFieldInterface } from 'components/Form/Form.types';
import TagsMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/TagsMultiSelectDropdown';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import MultiselectDropdown from 'components/DropdownSelect/MultiSelectDropdown/MultiselectDropdown';
import { getUsersName } from 'utils/formatting';
import { useUsersContext } from 'context/users.context';
import { useBrandsContext } from 'pages/Brands/brands.context';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';

const defaultStore: CreateStoreInterface = {
  storeNumber: '',
  name: '',
  // address: '',
  state: '',
  timezone: '',
  latlng: '',
  brandIds: [],
  userIds: [],
  tagIds: [],
  storeManagerUserIds: [],
};

const CreateStore = () => {
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { addStore } = useStoresContext();
  const { users } = useUsersContext();
  const { brands } = useBrandsContext();
  const [newStore, setNewStore] = useState<CreateStoreInterface>(defaultStore);
  const { ability } = useAuthContext();

  // Return null if user does not have 'Create' permissions
  if (!hasPermissions(ability, 'create', 'Store')) {
    return null;
  }

  const usersOptions: DropdownOption[] = users.map((user) => ({
    name: getUsersName(user),
    id: user.id,
  }));

  const brandsOptions: DropdownOption[] = brands.map((brand) => ({
    name: brand.name,
    id: brand.id,
  }));

  const handleAddStore = async () => {
    setLoading(true);
    await addStore(newStore);
    setNewStore(defaultStore);
    setShow(false);
    setLoading(false);
  };

  const handleUpdateField = (value: string | number, key: keyof Store) =>
    setNewStore({ ...newStore, [key]: value });

  const handleUpdateStoreBrands = (updatedBrands: DropdownOption[]) => {
    setNewStore({
      ...newStore,
      brandIds: updatedBrands.map((brand) => Number(brand.id)),
    });
  };

  const handleUpdateStoreUsers = (updatedUsers: DropdownOption[]) => {
    setNewStore({
      ...newStore,
      userIds: updatedUsers.map((user) => Number(user.id)),
    });
  };

  const handleUpdateStoreManagerUsers = (updatedUsers: DropdownOption[]) => {
    setNewStore({
      ...newStore,
      storeManagerUserIds: updatedUsers.map((user) => Number(user.id)),
    });
  };

  const handleUpdateTags = (updateTags: DropdownOption[]) => {
    setNewStore({
      ...newStore,
      tagIds: updateTags.map((tag) => Number(tag.id)),
    });
  };

  const fields: FormFieldInterface[] = [
    {
      label: 'Store Number',
      value: String(newStore.storeNumber),
      onChange: (val) => handleUpdateField(val, 'storeNumber'),
      fullWidth: true,
      validator: validateAlphaNumeric,
    },
    {
      label: 'Store Name',
      value: newStore.name,
      onChange: (val) => handleUpdateField(val, 'name'),
      fullWidth: true,
      requiredMinimumLength: entityNameMinimumLength,
      validator: validateEntityName,
    },
    // {
    //   label: 'Address',
    //   // value: newStore.address,
    //   onChange: (val) => handleUpdateField(val, 'address'),
    //   fullWidth: true,
    // },
    {
      label: 'State / Province',
      value: newStore.state,
      onChange: (val) => handleUpdateField(val, 'state'),
      fullWidth: true,
    },
    {
      label: 'Timezone',
      value: newStore.timezone,
      onChange: (val) => handleUpdateField(val, 'timezone'),
      fullWidth: true,
    },
    {
      label: 'Lat/Lng',
      value: newStore.latlng,
      onChange: (val) => handleUpdateField(val, 'latlng'),
      fullWidth: true,
    },
    {
      label: 'Brands',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Brands'
          allOptions={brandsOptions}
          fullWidth={true}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateStoreBrands(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Users',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Users'
          allOptions={usersOptions}
          fullWidth={true}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateStoreUsers(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Store Manager(s)',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Store Manager(s)'
          allOptions={usersOptions}
          fullWidth={true}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateStoreManagerUsers(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Tags',
      onChange: () => {},
      customField: <TagsMultiSelectDropdown onChange={handleUpdateTags} />,
    },
  ];

  return (
    <>
      <Button onClick={() => setShow(true)} variant='contained' color='secondary'>
        Create New Store
      </Button>
      <Dialog open={show} onClose={() => setShow(false)}>
        <Form
          fields={fields}
          title='New Store'
          onSubmit={handleAddStore}
          submitting={loading}
          buttonText='Create Store'
        />
      </Dialog>
    </>
  );
};

export default CreateStore;
